import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { keyframes } from '@emotion/react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Tooltip from '@mui/material/Tooltip';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FavoriteIcon from '@mui/icons-material/Favorite';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import AddIcon from '@mui/icons-material/Add';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import agent from '../../agent';
import BlastUploadDialog from './BlastUploadDialog';
import useMusicSync from '../../hooks/useMusicSync';
import { DEFAULT_THEME_COLOR, DEFAULT_GRADIENT } from '../../constants/theme';
import { getBlastLayout, formatBlastCount, blastObjectFit } from '../../utils/blasts';

const heartBurst = keyframes`
  0%   { transform: translate(-50%,-50%) scale(0) rotate(-20deg); opacity: 1; }
  40%  { transform: translate(-50%,-50%) scale(1.4) rotate(8deg);  opacity: 1; }
  70%  { transform: translate(-50%,-50%) scale(1.1) rotate(-4deg); opacity: 0.85; }
  100% { transform: translate(-50%,-80%) scale(0.8) rotate(0deg);  opacity: 0; }
`;

const likePop = keyframes`
  0%   { transform: scale(1); }
  35%  { transform: scale(1.35); }
  65%  { transform: scale(0.9); }
  100% { transform: scale(1); }
`;

const playPop = keyframes`
  from { opacity: 0; transform: translate(-50%,-50%) scale(1.4); }
  to   { opacity: 1; transform: translate(-50%,-50%) scale(1); }
`;

const discSpin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const flamePulse = keyframes`
  0%, 100% { transform: scale(1);    filter: drop-shadow(0 0 8px ${DEFAULT_THEME_COLOR}88); }
  50%       { transform: scale(1.18); filter: drop-shadow(0 0 20px ${DEFAULT_THEME_COLOR}cc); }
`;

const fadeSlideUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const PAGE_SIZE = 10;
const DOUBLE_TAP_MS = 280;
// Below the desktop breakpoint the fixed bottom navigation (58px) sits under the feed.
const BOTTOM_NAV_HEIGHT = 58;
const TEXT_SHADOW = '0 1px 6px rgba(0,0,0,0.95)';

const glass = {
  background: 'rgba(0,0,0,0.42)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: '1px solid rgba(255,255,255,0.1)',
};

/* ── One button of the right action rail ── */
const RailButton = ({ label, count, onClick, wide, children, ariaLabel }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.4 }}>
    <Tooltip title={wide ? (ariaLabel || '') : ''} placement="right">
      <IconButton
        aria-label={ariaLabel}
        onClick={(e) => { e.stopPropagation(); onClick?.(); }}
        sx={{
          width: 48,
          height: 48,
          color: 'white',
          background: wide ? 'rgba(255,255,255,0.12)' : 'transparent',
          filter: wide ? 'none' : 'drop-shadow(0 1px 4px rgba(0,0,0,0.6))',
          transition: 'transform 0.12s, background 0.2s',
          '&:hover': { background: wide ? 'rgba(255,255,255,0.2)' : 'transparent' },
          '&:active': { transform: 'scale(0.88)' },
        }}
      >
        {children}
      </IconButton>
    </Tooltip>
    {(count !== undefined || label) && (
      <Typography sx={{ color: 'white', fontSize: '0.75rem', fontWeight: 700, lineHeight: 1, textShadow: TEXT_SHADOW, maxWidth: 64, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {count !== undefined ? formatBlastCount(count) : label}
      </Typography>
    )}
  </Box>
);

/* ── Single blast item ── */
const BlastItem = ({
  reel, index, active, layout, muted, paused, onTogglePause, onToggleMute,
  onLike, onActive, feedRef, currentUser, onAuthRequired,
}) => {
  const navigate = useNavigate();
  const itemRef = useRef(null);
  const videoRef = useRef(null);
  const lastTapRef = useRef(0);
  const singleTapTimer = useRef(null);
  const [progress, setProgress] = useState(0);
  const [hearts, setHearts] = useState([]);
  const [fit, setFit] = useState('cover');
  const [captionOpen, setCaptionOpen] = useState(false);
  const [buffering, setBuffering] = useState(false);

  const wide = layout.mode === 'wide';
  const { hasMusic } = useMusicSync(videoRef, reel.musicUrl, reel.musicStartMs || 0, muted);
  // When a music track is attached the video's own audio is always muted and the
  // track plays over it; the shared mute toggle then controls the music instead.
  const videoMuted = hasMusic ? true : muted;

  // Report this item as the active one when most of it is on screen.
  useEffect(() => {
    const el = itemRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) onActive(index); },
      { root: feedRef.current, threshold: 0.6 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [index, onActive, feedRef]);

  // Play only the active blast; rewind the others.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (active && !paused) {
      video.play().catch(() => {});
    } else {
      video.pause();
      if (!active) {
        video.currentTime = 0;
        setProgress(0);
        setCaptionOpen(false);
      }
    }
  }, [active, paused]);

  useEffect(() => () => clearTimeout(singleTapTimer.current), []);

  const handleTap = (e) => {
    const now = Date.now();
    if (now - lastTapRef.current < DOUBLE_TAP_MS) {
      clearTimeout(singleTapTimer.current);
      lastTapRef.current = 0;
      if (!currentUser) { onAuthRequired?.(); return; }
      const rect = e.currentTarget.getBoundingClientRect();
      const heart = { id: now, x: e.clientX - rect.left, y: e.clientY - rect.top, tilt: Math.round(Math.random() * 40 - 20) };
      setHearts(h => [...h, heart]);
      setTimeout(() => setHearts(h => h.filter(x => x.id !== heart.id)), 900);
      if (!reel.isLiked) onLike(reel);
      return;
    }
    lastTapRef.current = now;
    singleTapTimer.current = setTimeout(() => onTogglePause(), DOUBLE_TAP_MS);
  };

  const seek = (e) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video?.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    video.currentTime = ratio * video.duration;
    setProgress(ratio * 100);
  };

  const goToAuthor = (e) => { e?.stopPropagation(); navigate(`/@${reel.author?.username}`); };
  const requireUser = (fn) => () => { if (!currentUser) { onAuthRequired?.(); return; } fn(); };

  const rail = (
    <Box
      sx={{
        position: 'absolute',
        zIndex: 6,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.75,
        ...(wide
          ? { left: 'calc(100% + 16px)', bottom: 8 }
          : { right: 6, bottom: 28 }),
      }}
    >
      <Box onClick={goToAuthor} sx={{ position: 'relative', cursor: 'pointer', mb: 1 }}>
        <Avatar
          src={reel.author?.imageUrl || reel.author?.image}
          alt={reel.author?.username}
          sx={{ width: 48, height: 48, border: '2px solid white', boxShadow: '0 2px 10px rgba(0,0,0,0.5)' }}
        />
        <Box sx={{
          position: 'absolute', bottom: -9, left: '50%', transform: 'translateX(-50%)',
          width: 20, height: 20, borderRadius: '50%', background: DEFAULT_GRADIENT,
          display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #000',
        }}>
          <AddIcon sx={{ fontSize: 13, color: '#0d1b2a' }} />
        </Box>
      </Box>

      <RailButton
        wide={wide}
        ariaLabel={reel.isLiked ? 'Unlike' : 'Like'}
        count={reel._count?.likes ?? 0}
        onClick={requireUser(() => onLike(reel))}
      >
        <FavoriteIcon
          key={reel.isLiked ? 'liked' : 'not-liked'}
          sx={{
            fontSize: 32,
            color: reel.isLiked ? '#fe2c55' : 'white',
            animation: reel.isLiked ? `${likePop} 0.4s ease-out` : 'none',
          }}
        />
      </RailButton>

      {reel.freak && (
        <RailButton
          wide={wide}
          ariaLabel={`Open ${reel.freak.title}`}
          label="Freak"
          onClick={() => navigate(`/freak/${reel.freak.slug}`)}
        >
          <LocalFireDepartmentIcon sx={{ fontSize: 30, color: DEFAULT_THEME_COLOR }} />
        </RailButton>
      )}

      <RailButton
        wide={wide}
        ariaLabel={muted ? 'Unmute' : 'Mute'}
        onClick={requireUser(onToggleMute)}
      >
        {muted ? <VolumeOffIcon sx={{ fontSize: 26 }} /> : <VolumeUpIcon sx={{ fontSize: 26, color: DEFAULT_THEME_COLOR }} />}
      </RailButton>

      {/* Spinning disc — TikTok's "sound" puck */}
      <Box
        onClick={(e) => { e.stopPropagation(); if (reel.musicLicenseUrl) window.open(reel.musicLicenseUrl, '_blank', 'noopener'); }}
        sx={{
          width: 44, height: 44, mt: 0.5, borderRadius: '50%',
          background: 'radial-gradient(circle, #333 0 30%, #111 31% 100%)',
          border: '6px solid #1c1c1c',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: `${discSpin} 5s linear infinite`,
          animationPlayState: active && !paused ? 'running' : 'paused',
          cursor: reel.musicLicenseUrl ? 'pointer' : 'default',
          overflow: 'hidden',
        }}
      >
        {hasMusic
          ? <MusicNoteIcon sx={{ fontSize: 16, color: 'white' }} />
          : <Avatar src={reel.author?.imageUrl || reel.author?.image} sx={{ width: 22, height: 22 }} />}
      </Box>
    </Box>
  );

  const info = (
    <Box
      sx={{
        position: 'absolute',
        left: 0,
        bottom: 0,
        right: wide ? 0 : 72,
        px: 2,
        pb: 2.5,
        zIndex: 5,
        animation: active ? `${fadeSlideUp} 0.4s ease-out` : 'none',
      }}
    >
      <Typography
        onClick={goToAuthor}
        sx={{ color: 'white', fontWeight: 800, fontSize: '1.02rem', textShadow: TEXT_SHADOW, cursor: 'pointer', display: 'inline-block', mb: 0.5, '&:hover': { textDecoration: 'underline' } }}
      >
        @{reel.author?.username}
      </Typography>

      {reel.caption && (
        <Typography
          onClick={(e) => { e.stopPropagation(); setCaptionOpen(o => !o); }}
          sx={{
            color: 'rgba(255,255,255,0.92)',
            fontSize: '0.9rem',
            lineHeight: 1.45,
            textShadow: TEXT_SHADOW,
            mb: 1,
            cursor: 'pointer',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            ...(captionOpen
              ? { maxHeight: '38vh', overflowY: 'auto' }
              : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }),
          }}
        >
          {reel.caption}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {reel.freak && (
          <Box
            onClick={(e) => { e.stopPropagation(); navigate(`/freak/${reel.freak.slug}`); }}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5, maxWidth: '100%',
              background: `${DEFAULT_THEME_COLOR}26`, border: `1px solid ${DEFAULT_THEME_COLOR}66`,
              backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
              borderRadius: 2, px: 1.1, py: 0.35, cursor: 'pointer',
              '&:hover': { background: `${DEFAULT_THEME_COLOR}40` },
            }}
          >
            <LocalFireDepartmentIcon sx={{ fontSize: 13, color: DEFAULT_THEME_COLOR }} />
            <Typography noWrap sx={{ color: 'white', fontSize: '0.75rem', fontWeight: 700 }}>
              {reel.freak.title}
            </Typography>
          </Box>
        )}

        {hasMusic && reel.musicName && (
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5, maxWidth: 240, overflow: 'hidden',
            background: 'rgba(0,0,0,0.35)', borderRadius: 2, px: 1.1, py: 0.35,
          }}>
            <MusicNoteIcon sx={{ fontSize: 13, color: 'white', flexShrink: 0 }} />
            <Typography noWrap sx={{ color: 'white', fontSize: '0.72rem', fontWeight: 600 }}>
              {reel.musicName}{reel.musicArtist ? ` — ${reel.musicArtist}` : ''}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Box
      ref={itemRef}
      data-testid="blast-item"
      sx={{
        height: '100%',
        width: '100%',
        scrollSnapAlign: 'start',
        scrollSnapStop: 'always',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        background: '#000',
        userSelect: 'none',
        WebkitUserSelect: 'none',
      }}
    >
      {/* Blurred backdrop behind the centered card (tablet / desktop) */}
      {wide && (
        <Box sx={{
          position: 'absolute', inset: -40,
          background: reel.thumbnailUrl
            ? `center / cover no-repeat url("${reel.thumbnailUrl}")`
            : `radial-gradient(circle at 50% 40%, ${DEFAULT_THEME_COLOR}33, #000 70%)`,
          filter: 'blur(40px) brightness(0.45) saturate(1.2)',
          pointerEvents: 'none',
        }} />
      )}

      {/* Card: full-bleed on phones, a 9:16 frame on wider screens */}
      <Box
        sx={{
          position: 'relative',
          width: wide ? layout.cardWidth : '100%',
          height: wide ? layout.cardHeight : '100%',
          borderRadius: wide ? 3 : 0,
          boxShadow: wide ? '0 20px 60px rgba(0,0,0,0.6)' : 'none',
          background: '#000',
          flexShrink: 0,
        }}
      >
        <Box
          onClick={handleTap}
          sx={{ position: 'absolute', inset: 0, overflow: 'hidden', borderRadius: 'inherit', cursor: 'pointer' }}
        >
          <video
            ref={videoRef}
            src={reel.videoUrl}
            poster={reel.thumbnailUrl}
            loop
            playsInline
            preload={active ? 'auto' : 'metadata'}
            muted={videoMuted}
            onLoadedMetadata={(e) => setFit(blastObjectFit(e.currentTarget.videoWidth, e.currentTarget.videoHeight))}
            onTimeUpdate={(e) => { const v = e.currentTarget; if (v.duration) setProgress((v.currentTime / v.duration) * 100); }}
            onWaiting={() => setBuffering(true)}
            onPlaying={() => setBuffering(false)}
            style={{ width: '100%', height: '100%', objectFit: fit, display: 'block' }}
          />

          {/* Readability gradients */}
          <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '45%', background: 'linear-gradient(0deg, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.3) 55%, transparent 100%)', pointerEvents: 'none' }} />
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 110, background: 'linear-gradient(180deg, rgba(0,0,0,0.45) 0%, transparent 100%)', pointerEvents: 'none' }} />

          {/* Paused indicator */}
          {active && paused && (
            <PlayArrowRoundedIcon sx={{
              position: 'absolute', top: '50%', left: '50%', fontSize: 96,
              color: 'rgba(255,255,255,0.8)', pointerEvents: 'none',
              filter: 'drop-shadow(0 2px 12px rgba(0,0,0,0.6))',
              animation: `${playPop} 0.18s ease-out forwards`,
            }} />
          )}

          {/* Buffering spinner */}
          {active && !paused && buffering && (
            <LocalFireDepartmentIcon sx={{
              position: 'absolute', top: '50%', left: '50%', marginLeft: '-24px', marginTop: '-24px',
              fontSize: 48, color: DEFAULT_THEME_COLOR, pointerEvents: 'none',
              animation: `${flamePulse} 1.2s ease-in-out infinite`,
            }} />
          )}

          {/* Double-tap hearts */}
          {hearts.map(h => (
            <Box key={h.id} sx={{ position: 'absolute', left: h.x, top: h.y, zIndex: 20, pointerEvents: 'none', rotate: `${h.tilt}deg` }}>
              <FavoriteIcon sx={{
                fontSize: 96, color: '#fe2c55', display: 'block',
                filter: 'drop-shadow(0 4px 18px rgba(254,44,85,0.7))',
                animation: `${heartBurst} 0.85s cubic-bezier(0.36,0.07,0.19,0.97) forwards`,
              }} />
            </Box>
          ))}

          {info}

          {/* Seekable progress bar */}
          <Box
            onClick={seek}
            role="slider"
            aria-label="Seek"
            aria-valuenow={Math.round(progress)}
            aria-valuemin={0}
            aria-valuemax={100}
            sx={{
              position: 'absolute', left: 0, right: 0, bottom: 0, height: 14, zIndex: 7,
              display: 'flex', alignItems: 'flex-end', cursor: 'pointer',
              '&:hover .bar': { height: 5 },
            }}
          >
            <Box className="bar" sx={{ width: '100%', height: 3, background: 'rgba(255,255,255,0.22)', transition: 'height 0.15s' }}>
              <Box sx={{ height: '100%', width: `${progress}%`, background: DEFAULT_GRADIENT, transition: 'width 0.25s linear' }} />
            </Box>
          </Box>
        </Box>

        {rail}
      </Box>
    </Box>
  );
};

/* ── Auth prompt for logged-out users ── */
const AuthPromptDialog = ({ open, onClose }) => {
  const navigate = useNavigate();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, #0d1b2a 0%, #1a0d0d 100%)',
          border: `1px solid ${DEFAULT_THEME_COLOR}33`,
          borderRadius: 4,
          p: { xs: 3, sm: 4 },
          mx: 2,
          maxWidth: 380,
          textAlign: 'center',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
        <LocalFireDepartmentIcon sx={{ fontSize: 48, color: DEFAULT_THEME_COLOR }} />
      </Box>
      <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '1.25rem', mb: 1 }}>
        Join the adventure
      </Typography>
      <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem', mb: 3 }}>
        Create an account or log in to like Blasts and turn the sound on.
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/register')}
          sx={{ background: DEFAULT_GRADIENT, color: '#0d1b2a', fontWeight: 700, borderRadius: 3, '&:hover': { filter: 'brightness(1.08)', background: DEFAULT_GRADIENT } }}
        >
          Create account
        </Button>
        <Button
          variant="outlined"
          size="large"
          onClick={() => navigate('/login')}
          sx={{ borderColor: DEFAULT_THEME_COLOR, color: DEFAULT_THEME_COLOR, fontWeight: 700, borderRadius: 3, '&:hover': { background: `${DEFAULT_THEME_COLOR}18`, borderColor: DEFAULT_THEME_COLOR } }}
        >
          Log in
        </Button>
        <Button
          onClick={onClose}
          sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600, '&:hover': { background: 'rgba(255,255,255,0.05)' } }}
        >
          Keep watching
        </Button>
      </Box>
    </Dialog>
  );
};

/* ── Feed size: tracks the scroll container so cards fit any viewport ── */
const useElementSize = (el) => {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    if (!el) return undefined;
    const update = () => setSize(prev => (
      prev.width === el.clientWidth && prev.height === el.clientHeight
        ? prev
        : { width: el.clientWidth, height: el.clientHeight }
    ));
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [el]);
  return size;
};

/* ── Main view ── */
export const BlastsView = ({ leftNavigationOpen, currentUser }) => {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery({ minWidth: 992 });
  const paddingLeft = (isDesktop && leftNavigationOpen) ? 300 : (isDesktop ? 75 : 0);
  // The bottom navigation is only rendered below the desktop breakpoint.
  const viewHeight = isDesktop ? '100dvh' : `calc(100dvh - ${BOTTOM_NAV_HEIGHT}px - env(safe-area-inset-bottom, 0px))`;

  const feedRef = useRef(null);
  const [feedEl, setFeedEl] = useState(null);
  const attachFeed = useCallback((el) => { feedRef.current = el; setFeedEl(el); }, []);
  const feedSize = useElementSize(feedEl);
  const layout = getBlastLayout(feedSize);
  const wide = layout.mode === 'wide';

  const [reels, setReels] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [authPromptOpen, setAuthPromptOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [mutedPref, setMutedPref] = useState(true);
  // Logged-out users can only watch: sound stays off with no way to turn it on.
  const muted = currentUser ? mutedPref : true;

  const loadPage = useCallback(async (p) => {
    try {
      const res = await agent.Reels.all(p);
      const data = res.reels || [];
      if (p === 0) setReels(data);
      else setReels(prev => [...prev, ...data]);
      if (data.length < PAGE_SIZE) setHasMore(false);
    } catch {}
  }, []);

  useEffect(() => {
    setLoading(true);
    loadPage(0).finally(() => setLoading(false));
  }, []);

  const handleLoadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const nextPage = page + 1;
    setPage(nextPage);
    await loadPage(nextPage);
    setLoadingMore(false);
  }, [loadingMore, hasMore, page, loadPage]);

  // Prefetch the next page a couple of blasts before the end.
  useEffect(() => {
    if (reels.length && activeIndex >= reels.length - 3) handleLoadMore();
  }, [activeIndex, reels.length]);

  const handleActive = useCallback((i) => setActiveIndex(i), []);

  // A new blast always starts playing.
  useEffect(() => setPaused(false), [activeIndex]);

  const scrollToIndex = useCallback((i) => {
    const feed = feedRef.current;
    if (!feed) return;
    const target = Math.max(0, Math.min(reels.length - 1, i));
    feed.scrollTo({ top: target * feed.clientHeight, behavior: 'smooth' });
  }, [reels.length]);

  const toggleMute = useCallback(() => setMutedPref(m => !m), []);
  const togglePause = useCallback(() => setPaused(p => !p), []);

  // Keyboard: ↑/↓ (or K/J) browse, Space pauses, M mutes.
  useEffect(() => {
    const onKey = (e) => {
      if (uploadOpen || authPromptOpen) return;
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
      switch (e.key) {
        case 'ArrowDown': case 'PageDown': case 'j':
          e.preventDefault(); scrollToIndex(activeIndex + 1); break;
        case 'ArrowUp': case 'PageUp': case 'k':
          e.preventDefault(); scrollToIndex(activeIndex - 1); break;
        case ' ':
          e.preventDefault(); togglePause(); break;
        case 'm':
          if (currentUser) toggleMute(); else setAuthPromptOpen(true);
          break;
        default:
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeIndex, scrollToIndex, togglePause, toggleMute, currentUser, uploadOpen, authPromptOpen]);

  const handleLike = useCallback(async (reel) => {
    const liked = !reel.isLiked;
    const apply = (isLiked) => setReels(prev => prev.map(r => r.slug === reel.slug
      ? { ...r, isLiked, _count: { ...r._count, likes: Math.max(0, (r._count?.likes ?? 0) + (isLiked ? 1 : -1)) } }
      : r));
    apply(liked); // optimistic
    try {
      if (liked) await agent.Reels.like(reel.slug);
      else await agent.Reels.unlike(reel.slug);
    } catch {
      apply(!liked);
    }
  }, []);

  const handleUploadSuccess = useCallback(async () => {
    setPage(0);
    setHasMore(true);
    setLoading(true);
    await loadPage(0);
    setLoading(false);
    setActiveIndex(0);
    feedRef.current?.scrollTo({ top: 0 });
  }, [loadPage]);

  const centered = {
    height: viewHeight,
    background: '#000',
    paddingLeft: `${paddingLeft}px`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    position: 'relative',
    px: 2,
  };

  /* ── Loading state ── */
  if (loading) {
    return (
      <Box sx={centered}>
        <LocalFireDepartmentIcon sx={{
          fontSize: 64,
          color: DEFAULT_THEME_COLOR,
          animation: `${flamePulse} 1.4s ease-in-out infinite`,
        }} />
        <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.875rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
          Loading Blasts
        </Typography>
      </Box>
    );
  }

  /* ── Empty state ── */
  if (reels.length === 0) {
    return (
      <Box sx={centered}>
        <IconButton onClick={() => navigate(-1)} aria-label="Back" sx={{ position: 'absolute', top: 16, left: 16 + paddingLeft, color: 'white', background: 'rgba(255,255,255,0.08)', '&:hover': { background: 'rgba(255,255,255,0.15)' } }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ animation: `${flamePulse} 2s ease-in-out infinite` }}>
          <LocalFireDepartmentIcon sx={{ fontSize: 72, color: DEFAULT_THEME_COLOR }} />
        </Box>
        <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1.25rem' }}>No Blasts yet</Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.875rem', textAlign: 'center', maxWidth: 280 }}>
          Blasts are short videos attached to Freaks. Go on an adventure and post one!
        </Typography>
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/freak')}
            sx={{ borderColor: DEFAULT_THEME_COLOR, color: DEFAULT_THEME_COLOR, borderRadius: 3, px: 3, '&:hover': { background: `${DEFAULT_THEME_COLOR}18` } }}
          >
            Browse Freaks
          </Button>
          {currentUser && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setUploadOpen(true)}
              sx={{
                background: DEFAULT_GRADIENT,
                color: '#0d1b2a',
                fontWeight: 700,
                borderRadius: 3,
                px: 3,
                '&:hover': { filter: 'brightness(1.08)' },
              }}
            >
              Add Blast
            </Button>
          )}
        </Box>

        <BlastUploadDialog
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          onSuccess={handleUploadSuccess}
        />
      </Box>
    );
  }

  /* ── Feed ── */
  return (
    <Box sx={{ height: viewHeight, overflow: 'hidden', background: '#000', paddingLeft: `${paddingLeft}px`, position: 'relative' }}>
      <Box sx={{ position: 'relative', height: '100%' }}>

        {/* Top bar: back · title · add */}
        <Box sx={{
          position: 'absolute', top: 0, left: 0, right: 0, zIndex: 30,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 1.75, pt: 1.75, pointerEvents: 'none',
          '& > *': { pointerEvents: 'auto' },
        }}>
          <IconButton
            onClick={() => navigate(-1)}
            aria-label="Back"
            sx={{ ...glass, color: 'white', width: 40, height: 40, '&:hover': { background: 'rgba(0,0,0,0.7)' } }}
          >
            <ArrowBackIcon sx={{ fontSize: 20 }} />
          </IconButton>

          <Box sx={{ ...glass, display: 'flex', alignItems: 'center', gap: 0.75, borderRadius: '20px', px: 1.75, py: 0.65 }}>
            <LocalFireDepartmentIcon sx={{ color: DEFAULT_THEME_COLOR, fontSize: 17 }} />
            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.88rem', letterSpacing: '0.05em' }}>
              Blasts
            </Typography>
          </Box>

          {currentUser ? (
            wide ? (
              <Button
                onClick={() => setUploadOpen(true)}
                startIcon={<AddIcon />}
                sx={{
                  background: DEFAULT_GRADIENT, color: '#0d1b2a', fontWeight: 800, borderRadius: '20px', px: 2, height: 40,
                  boxShadow: `0 6px 24px ${DEFAULT_THEME_COLOR}55`,
                  '&:hover': { filter: 'brightness(1.1)', background: DEFAULT_GRADIENT },
                }}
              >
                Add Blast
              </Button>
            ) : (
              <IconButton
                onClick={() => setUploadOpen(true)}
                aria-label="Add Blast"
                sx={{ background: DEFAULT_GRADIENT, color: '#0d1b2a', width: 40, height: 40, '&:hover': { background: DEFAULT_GRADIENT, filter: 'brightness(1.1)' } }}
              >
                <AddIcon />
              </IconButton>
            )
          ) : <Box sx={{ width: 40 }} />}
        </Box>

        {/* Scroll feed */}
        <Box
          ref={attachFeed}
          sx={{
            height: '100%',
            overflowY: 'scroll',
            scrollSnapType: 'y mandatory',
            overscrollBehavior: 'contain',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          {reels.map((reel, i) => (
            <BlastItem
              key={reel.slug || reel.id || i}
              reel={reel}
              index={i}
              active={i === activeIndex}
              layout={layout}
              muted={muted}
              paused={paused}
              onTogglePause={togglePause}
              onToggleMute={toggleMute}
              onLike={handleLike}
              onActive={handleActive}
              feedRef={feedRef}
              currentUser={currentUser}
              onAuthRequired={() => setAuthPromptOpen(true)}
            />
          ))}

          {/* Loading-more placeholder */}
          {loadingMore && (
            <Box sx={{
              height: '100%', background: '#000', display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', scrollSnapAlign: 'start', gap: 2,
            }}>
              <LocalFireDepartmentIcon sx={{ fontSize: 48, color: DEFAULT_THEME_COLOR, animation: `${flamePulse} 1.2s ease-in-out infinite` }} />
              <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.8rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                Loading more
              </Typography>
            </Box>
          )}
        </Box>

        {/* Up / down navigation (tablet / desktop) */}
        {layout.showArrows && (
          <Box sx={{
            position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)', zIndex: 30,
            display: 'flex', flexDirection: 'column', gap: 1.5,
          }}>
            {[
              { dir: -1, label: 'Previous blast', Icon: KeyboardArrowUpRoundedIcon, disabled: activeIndex === 0 },
              { dir: 1, label: 'Next blast', Icon: KeyboardArrowDownRoundedIcon, disabled: activeIndex >= reels.length - 1 },
            ].map(({ dir, label, Icon, disabled }) => (
              <IconButton
                key={label}
                aria-label={label}
                disabled={disabled}
                onClick={() => scrollToIndex(activeIndex + dir)}
                sx={{
                  ...glass, width: 48, height: 48, color: 'white',
                  '&:hover': { background: 'rgba(255,255,255,0.18)' },
                  '&.Mui-disabled': { color: 'rgba(255,255,255,0.25)', background: 'rgba(0,0,0,0.25)' },
                }}
              >
                <Icon sx={{ fontSize: 30 }} />
              </IconButton>
            ))}
          </Box>
        )}
      </Box>

      <BlastUploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onSuccess={handleUploadSuccess}
      />

      <AuthPromptDialog open={authPromptOpen} onClose={() => setAuthPromptOpen(false)} />
    </Box>
  );
};

const mapStateToProps = state => ({
  leftNavigationOpen: state.common.leftNavigationOpen,
  currentUser: state.common.currentUser,
});

export default connect(mapStateToProps)(BlastsView);
